import { fillBasemapThumbnails } from "./basemap-thumbnails";
import { DEMO_LABEL, DEMO_NOW, demoAppPath, isDemoPath } from "../shared/demo";
import { demoFires, demoJsonPath, installDemoChrome } from "./demo";
import { mtgGeoCoordinates, mtgGeoFrameUrl } from "../shared/imagery";
import { eventBurnedFrame, replayProductSlotIso, utcIsoDate } from "../shared/sources";
import { chipCounts, visibleIncidents } from "../shared/event-filter";
import { decimateHeat, heatPointsFromDetections, mergeHeatPoints, parseHeatPoints, type HeatPoint } from "../shared/heat";
import { eventLookMarks, firstLookInWindow, nextPlayheadAfterRefresh } from "../shared/playhead";
import { effisFwiInfoUrl, fwiClass, parseEffisFwiHtml } from "../shared/fwi";
import { bootWindow, DEFAULT_VIEW, formatPermalink, formatPermalinkTime, parsePermalink, type PlaceKey, type WindowKey } from "../shared/permalink";
import type { MapView } from "../shared/event-state";
import { cardsFromIncidents, currentEventMonth, detailFromCatalog, firesFromDetail, type EventCard } from "../shared/catalog";
import { eventsCatalogPath, eventsTheaterPath, legacyEventsRedirect, parseAppPath } from "../shared/route";
import { eventSeoFromDetail, pageMeta } from "../shared/seo";
import { publicOriginFromHost } from "../shared/sources";
import type { AlertRow, FiresResponse, Incident, IncidentDetail, WebcamLink, WindSample } from "../shared/types";
import { formatClock, updatedLabel } from "./format";
import {
  ensureNotifyPermission,
  followShareUrl,
  followToken,
  loadAlerts,
  loadFollows,
  readSeenAlertIds,
  setFollow,
  unsetFollow,
  writeSeenAlertIds,
  type FollowRecord,
} from "./follow";
import {
  applyData,
  createMap,
  EVENT_HEAT_ZOOM,
  EVENT_SAT_ZOOM,
  flyToIncident,
  flyToIncidentHeat,
  incidentHeatPoints,
  locateHere,
  pickIncidentId,
  PLACES,
  setEventBurned,
  setEventSat,
  setFwiTime,
  setLayerVisible,
} from "./map";
import { installTips } from "./tips";
import { fillEventPlaceNames, renderEventsList } from "./events";
import { applyDocumentMeta } from "./seo";
import { styleBurnedOverlay } from "./burned-style";
import { refreshTranslation } from "./translate";
import { applyCamerasUpdate, applyFwiUpdate, applyPlayheadUpdate, applyWindUpdate, renderPanel, stopPanel } from "./ui";

const demoMode = isDemoPath(location.pathname);
const dataNow = () => demoMode ? DEMO_NOW : Date.now();
const appPath = (path: string) => demoMode ? demoAppPath(path) : path;
const boot = parsePermalink(location.hash);
{
  // Old `#e=1` sheet → `/events` (and `#e=1&id=` → the theater).
  const dest = legacyEventsRedirect(location.pathname, location.search, location.hash);
  if (dest) history.replaceState(null, "", appPath(dest));
}
const bootRoute = parseAppPath(location.pathname, location.search);

const state = {
  page: bootRoute.page as "map" | "events" | "theater",
  window: (demoMode ? (boot.window === "1h" || boot.window === "6h" ? boot.window : "24h") : bootWindow(boot)) as WindowKey,
  place: (boot.place ?? null) as PlaceKey | null,
  view: (boot.view ?? DEFAULT_VIEW) as MapView,
  data: null as FiresResponse | null,
  playheadMs: dataNow(),
  playing: false,
  layers: { fire: true, sat: false, risk: false, lst: false, fwi: false, ba: false, wind: false },
  fwiDay: 0,
  follows: [] as FollowRecord[],
  alerts: [] as AlertRow[],
  openId: null as string | null,
  detail: null as IncidentDetail | null,
  eventCards: [] as EventCard[],
  eventsCountry: bootRoute.page === "events" ? bootRoute.country : "",
  eventsMonth: bootRoute.page === "events" && bootRoute.month ? bootRoute.month : currentEventMonth(dataNow()),
  eventsQuery: bootRoute.page === "events" ? bootRoute.q : "",
};

const map = createMap(document.getElementById("map")!);
const panel = document.getElementById("panel")!;
const drawer = document.getElementById("alerts-drawer")!;
const eventsPage = document.getElementById("events-page")!;
const theaterBar = document.getElementById("theater-bar")!;
const theaterTitle = document.getElementById("theater-title")!;
const theaterLive = document.getElementById("theater-live") as HTMLAnchorElement;
const lightbox = document.getElementById("lightbox")!;
const mailPop = document.getElementById("mail-pop")!;
const mailInput = document.getElementById("mail-pop-email") as HTMLInputElement;

/** Bumps when the open incident changes so in-flight camera/wind fetches are ignored. */
let panelGen = 0;
let windTimer = 0;

if (!demoMode) followToken();
if (demoMode) installDemoChrome();
installTips();

let pendingOpenId = bootRoute.page === "map" ? (boot.id ?? null) : null;
let pendingTheaterId = bootRoute.page === "theater" ? bootRoute.id : null;
/** Shared `#t=` — applied once the fires / theater window is loaded. */
let pendingPlayheadMs = parsePermalink(location.hash).t ?? null;
let firesEtag = "";
/** Last /api/fires payload included the unranked High Sensitivity slice. */
let firesSensitive = false;
/** Heat tuples parsed once per 200 — Play fades these without Date.parse. */
let parsedHeat: HeatPoint[] = [];
let refreshInFlight = false;
let lastRefreshAt = 0;
let lastPaintAt = 0;
let paintRaf = 0;
let lastChipKey = "";
let lastSatKey = "";
let lastBurnedKey = "";
let lastMarksKey = "";
let burnedGen = 0;
/** ~12 fps during Play — MapLibre heatmap does not need 60 setData/sec. */
const PLAY_PAINT_MS = 80;
/** High Sensitivity trail cap while the playhead is moving. */
const PLAY_HEAT_MAX = 4000;

map.on("load", () => {
  const winBtn = document.querySelector(`[data-window="${state.window}"]`) as HTMLButtonElement | null;
  if (winBtn) setOn("#windows button", winBtn);
  syncPlaceButtons();
  if (boot.place) {
    const p = PLACES[boot.place];
    map.jumpTo({ center: p.center, zoom: p.zoom });
  } else if (!boot.id) {
    // Shared #p= / #id= wins. Otherwise keep the Europe home camera — no location prompt.
    state.place = "eu";
    syncPlaceButtons();
  }
  syncViewButtons();
  syncPageChrome();
  if (location.hostname.includes("staging")) {
    document.getElementById("staging-chip")?.classList.remove("hidden");
    document.body.classList.add("staging");
  }
  if (state.page === "events") void loadEvents();
  else if (state.page === "theater" && pendingTheaterId) void openTheater(pendingTheaterId);
  else void refresh();
  void refreshFollows();
  void refreshAlerts();
  setInterval(() => {
    if (!demoMode && !document.hidden && state.page === "map") void refresh();
  }, 180_000);
  setInterval(() => {
    if (!demoMode && !document.hidden) void refreshAlerts();
  }, 120_000);
});

map.on("click", (e) => {
  if (!state.data) return;
  const visible = filteredIncidents();
  const id = pickIncidentId(map, e.point, e.lngLat, visible);
  if (id) {
    hideFwiProbe();
    void openIncident(id, undefined, { autoplay: true });
    return;
  }
  if (state.layers.fwi) void showFwiProbe(e.lngLat.lng, e.lngLat.lat);
});
for (const layer of ["incident-dot", "incident-halo", "incident-hit", "heat"]) {
  map.on("mouseenter", layer, () => {
    map.getCanvas().style.cursor = "pointer";
  });
  map.on("mouseleave", layer, () => {
    map.getCanvas().style.cursor = "";
  });
}

document.getElementById("windows")!.addEventListener("click", (ev) => {
  const btn = (ev.target as HTMLElement).closest("button[data-window]") as HTMLButtonElement | null;
  if (!btn) return;
  setWindow(btn.dataset.window as WindowKey);
});

document.getElementById("zoom-in")!.addEventListener("click", () => {
  map.zoomIn({ duration: 250 });
});
document.getElementById("zoom-out")!.addEventListener("click", () => {
  map.zoomOut({ duration: 250 });
});
document.getElementById("locate-me")!.addEventListener("click", (ev) => {
  locateHere(map, ev.currentTarget as HTMLElement);
});

document.getElementById("layers")!.addEventListener("click", (ev) => {
  const btn = (ev.target as HTMLElement).closest("button[data-layer]") as HTMLButtonElement | null;
  if (!btn) return;
  const layer = btn.dataset.layer as keyof typeof state.layers;
  setMapLayer(layer, !state.layers[layer]);
});

/** Sync the layer button, MapLibre visibility, and the note under the time slider. */
function setMapLayer(layer: keyof typeof state.layers, on: boolean): void {
  if (demoMode && layer !== "fire") return;
  state.layers[layer] = on;
  document.querySelector(`#layers button[data-layer="${layer}"]`)?.classList.toggle("on", on);
  setLayerVisible(map, layer, on);
  document.getElementById("heat-note")!.classList.toggle("hidden", !state.layers.lst);
  document.getElementById("fwi-note")!.classList.toggle("hidden", !state.layers.fwi);
  document.getElementById("fwi-days")!.classList.toggle("hidden", !state.layers.fwi);
  document.getElementById("fwi-scale")!.classList.toggle("hidden", !state.layers.fwi);
  document.getElementById("ba-note")!.classList.toggle("hidden", !state.layers.ba);
  document.getElementById("sat-note")!.classList.toggle("hidden", !state.layers.sat);
  document.getElementById("wind-note")!.classList.toggle("hidden", !state.layers.wind);
  if (layer === "sat") syncEventSat();
  // Burned on/off decides whether the local scar would double-paint.
  if (layer === "ba") syncEventBurned();
  syncBurnedLegend();
  if (!state.layers.fwi) hideFwiProbe();
}

document.getElementById("fwi-days")!.addEventListener("click", (ev) => {
  const btn = (ev.target as HTMLElement).closest("button[data-fwi-day]") as HTMLButtonElement | null;
  if (!btn) return;
  state.fwiDay = Number(btn.dataset.fwiDay);
  setOn("#fwi-days button", btn);
  setFwiTime(map, utcIsoDate(state.fwiDay));
});

document.getElementById("places")!.addEventListener("click", (ev) => {
  const btn = (ev.target as HTMLElement).closest("button[data-place]") as HTMLButtonElement | null;
  if (!btn) return;
  const place = btn.dataset.place as PlaceKey;
  state.place = place;
  setOn("#places button[data-place]", btn);
  const p = PLACES[place];
  map.flyTo({ center: p.center, zoom: p.zoom, speed: 0.9 });
  writeHash();
});

document.getElementById("stats")!.addEventListener("click", (ev) => {
  const btn = (ev.target as HTMLElement).closest("button[data-view]") as HTMLButtonElement | null;
  if (!btn) return;
  const next = (btn.dataset.view || "") as MapView | "";
  // Exclusive — one chip, one list. Clicking Fires again stays on Fires.
  if (
    next === "fires" ||
    next === "suspected" ||
    next === "campaigns" ||
    next === "rising" ||
    next === "new" ||
    next === "fine"
  ) {
    state.view = next;
  }
  syncViewButtons();
  writeHash();
  // Default poll is the hottest 2500 — refetch so France's small looks are in the payload.
  if (state.view === "fine" && !firesSensitive) {
    firesEtag = "";
    void refresh();
    return;
  }
  paint();
});

document.addEventListener("visibilitychange", () => {
  // Skip a focus refetch if the 3 min poll just ran.
  if (!demoMode && !document.hidden && state.page === "map" && Date.now() - lastRefreshAt > 60_000) void refresh();
});

document.getElementById("play")!.addEventListener("click", () => {
  if (state.playing) stopPlay();
  else startPlay();
});

const slider = document.getElementById("slider") as HTMLInputElement;
slider.addEventListener("input", () => {
  if (!state.data) return;
  stopPlay();
  const t0 = Date.parse(state.data.window.since);
  const t1 = Date.parse(state.data.window.until);
  state.playheadMs = t0 + (Number(slider.value) / 1000) * (t1 - t0);
  paint();
});
slider.addEventListener("change", () => {
  writeHash();
});
document.getElementById("playhead")!.addEventListener("click", () => {
  jumpToNow();
});

document.getElementById("watch-be")!.addEventListener("click", () => void toggleBelgium());
document.getElementById("watch-be-drawer")!.addEventListener("click", () => void toggleBelgium());
const attrBtn = document.getElementById("attr-btn")!;
const attrPop = document.getElementById("attr-pop")!;

function setAttrOpen(on: boolean): void {
  attrPop.classList.toggle("hidden", !on);
  attrBtn.setAttribute("aria-expanded", on ? "true" : "false");
}

attrBtn.addEventListener("click", (ev) => {
  ev.stopPropagation();
  setAttrOpen(attrPop.classList.contains("hidden"));
});
document.addEventListener("click", (ev) => {
  if (attrPop.classList.contains("hidden")) return;
  const t = ev.target as Node;
  if (attrBtn.contains(t) || attrPop.contains(t)) return;
  setAttrOpen(false);
});

document.getElementById("alerts-btn")!.addEventListener("click", () => {
  drawer.classList.toggle("hidden");
});
document.getElementById("alerts-close")!.addEventListener("click", () => drawer.classList.add("hidden"));
document.getElementById("nav-home")!.addEventListener("click", (ev) => {
  ev.preventDefault();
  go("/");
});
document.getElementById("nav-map")!.addEventListener("click", (ev) => {
  ev.preventDefault();
  go("/");
});
document.getElementById("nav-events")!.addEventListener("click", (ev) => {
  ev.preventDefault();
  go(eventsListPath());
});
document.getElementById("theater-back")!.addEventListener("click", () => {
  go(eventsListPath());
});
theaterTitle.addEventListener("click", () => {
  if (!state.detail) return;
  panel.classList.remove("hidden");
  document.body.classList.add("incident-open");
});
theaterLive.addEventListener("click", (ev) => {
  ev.preventDefault();
  if (!state.openId) return;
  go(`/${formatPermalink({ id: state.openId })}`);
});
eventsPage.addEventListener("change", (ev) => {
  const sel = (ev.target as HTMLElement).closest("select[data-filter]") as HTMLSelectElement | null;
  if (sel?.dataset.filter !== "month") return;
  state.eventsMonth = sel.value || "all";
  writeEventsUrl();
  paintEvents();
});
eventsPage.addEventListener("input", (ev) => {
  const input = ev.target as HTMLInputElement;
  if (input.dataset.filter !== "q") return;
  state.eventsQuery = input.value;
  writeEventsUrl();
  paintEvents();
});
eventsPage.addEventListener("click", (ev) => {
  const country = (ev.target as HTMLElement).closest("button[data-country]") as HTMLButtonElement | null;
  if (country) {
    state.eventsCountry = country.dataset.country ?? "";
    writeEventsUrl();
    paintEvents();
    return;
  }
  const btn = (ev.target as HTMLElement).closest("button[data-id]") as HTMLButtonElement | null;
  if (!btn?.dataset.id) return;
  go(eventsTheaterPath(btn.dataset.id));
});
window.addEventListener("popstate", () => {
  void applyRoute();
});
document.getElementById("lightbox-close")!.addEventListener("click", closeLightbox);
document.getElementById("mail-pop-dismiss")!.addEventListener("click", closeMailPop);
document.getElementById("mail-pop-cancel")!.addEventListener("click", closeMailPop);
document.getElementById("mail-pop-stop")!.addEventListener("click", () => {
  void stopIncidentAlerts();
});
document.getElementById("mail-pop-form")!.addEventListener("submit", (ev) => {
  ev.preventDefault();
  void saveMailAlert();
});
document.getElementById("mail-pop-share")!.addEventListener("click", () => {
  void copyText(followShareUrl());
});
document.addEventListener("keydown", (ev) => {
  if (ev.key === "Escape") {
    // Close the email dialog first so Escape does not also dump the panel.
    if (!mailPop.classList.contains("hidden")) {
      closeMailPop();
      return;
    }
    if (!attrPop.classList.contains("hidden")) {
      setAttrOpen(false);
      return;
    }
    closeLightbox();
    if (state.page === "theater") {
      go(eventsListPath());
      return;
    }
    if (ev.target instanceof HTMLInputElement || ev.target instanceof HTMLTextAreaElement) {
      (ev.target as HTMLInputElement).blur();
      return;
    }
    closeIncident();
    drawer.classList.add("hidden");
    hideFwiProbe();
    return;
  }
  const typing = ev.target instanceof HTMLInputElement || ev.target instanceof HTMLTextAreaElement;
  if (typing) return;
  if (ev.key === "]" || ev.key === "[") {
    ev.preventDefault();
    cycleIncident(ev.key === "]" ? 1 : -1);
    return;
  }
  const win = WINDOW_KEYS[ev.key];
  if (win && state.page === "map") {
    ev.preventDefault();
    setWindow(win);
  }
});

panel.addEventListener("click", (ev) => {
  const t = ev.target as HTMLElement;
  if (t.closest("#panel-close")) {
    closeIncident();
    return;
  }
  // Class pill is a jump link — Details lives below snapshots / cameras.
  if (t.closest("#panel-class")) {
    panel.querySelector("#panel-details")?.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }
  if (t.closest("#panel-copy-link")) {
    void copyText(location.href);
    return;
  }
  const coordsBtn = t.closest("#panel-copy-coords") as HTMLButtonElement | null;
  if (coordsBtn?.dataset.coords) {
    void copyText(coordsBtn.dataset.coords);
    return;
  }
  const followBtn = t.closest("#panel-follow") as HTMLButtonElement | null;
  if (followBtn) {
    // Bell is follow + email — one click arms browser notifications and opens the address popup.
    void followAndAlert(followBtn.dataset.id!);
    return;
  }
  const thumb = t.closest(".thumb") as HTMLElement | null;
  if (thumb?.dataset.full) {
    openLightbox(thumb.dataset.full, thumb.dataset.caption ?? "");
    return;
  }
  const sensor = t.closest(".sensor-row") as HTMLElement | null;
  if (sensor?.dataset.source) {
    const src = sensor.dataset.source;
    panel.querySelectorAll(".timeline li").forEach((li) => {
      (li as HTMLElement).classList.toggle("dim", (li as HTMLElement).dataset.source !== src);
    });
    panel.querySelectorAll(".sensor-row").forEach((row) => row.classList.toggle("on", row === sensor));
    return;
  }
  const nearbyBtn = t.closest(".nearby-fire") as HTMLButtonElement | null;
  if (nearbyBtn?.dataset.id) {
    void openIncident(nearbyBtn.dataset.id);
  }
});

document.getElementById("alerts-list")!.addEventListener("click", (ev) => {
  const btn = (ev.target as HTMLElement).closest("button[data-incident]") as HTMLButtonElement | null;
  if (!btn) return;
  drawer.classList.add("hidden");
  go(`/${formatPermalink({ id: btn.dataset.incident })}`);
});

function firesQuery(): string {
  const q = new URLSearchParams({ window: state.window });
  if (state.view === "fine") q.set("sensitive", "1");
  return q.toString();
}

async function refresh(): Promise<void> {
  if (document.hidden || refreshInFlight || state.page !== "map") return;
  refreshInFlight = true;
  try {
    const wantSensitive = state.view === "fine";
    // Do not 304 a hottest-2500 body when High Sensitivity needs the full trail.
    if (wantSensitive !== firesSensitive) firesEtag = "";
    const headers: HeadersInit = {};
    if (firesEtag) headers["If-None-Match"] = firesEtag;
    const res = demoMode
      ? new Response(JSON.stringify(await demoFires(state.window)), { headers: { "content-type": "application/json" } })
      : await fetch(`/api/fires?${firesQuery()}`, { headers });
    lastRefreshAt = Date.now();
    if (res.status === 304) {
      // Payload unchanged — only the "updated" clock, not a GeoJSON rebuild.
      if (state.data) {
        document.getElementById("updated")!.textContent = updatedLabel(state.data.updated_at, Date.now());
      }
      return;
    }
    if (!res.ok) return;
    firesEtag = res.headers.get("etag") ?? "";
    firesSensitive = wantSensitive;
    const next = (await res.json()) as FiresResponse;
    const t0 = Date.parse(next.window.since);
    const t1 = Date.parse(next.window.until);
    state.playheadMs = nextPlayheadAfterRefresh({
      playing: state.playing,
      sliderPermille: Number(slider.value),
      previousPlayheadMs: state.playheadMs,
      windowSinceMs: t0,
      windowUntilMs: t1,
    });
    slider.value = String(Math.round(((state.playheadMs - t0) / Math.max(t1 - t0, 1)) * 1000));
    state.data = next;
    parsedHeat = parseHeatPoints(next.heat ?? []);
    // Seed Events from the map list so the catalog is never empty if /api/events fails.
    if (state.eventCards.length === 0) {
      state.eventCards = cardsFromIncidents(state.data.incidents, dataNow());
    }
    paint({ force: true });
    if (pendingPlayheadMs != null) {
      const at = pendingPlayheadMs;
      if (at >= t0 && at <= t1) rewindPlayhead(at);
    }
    if (pendingOpenId) {
      const id = pendingOpenId;
      pendingOpenId = null;
      const at = pendingPlayheadMs;
      pendingPlayheadMs = null;
      void openIncident(id, undefined, { playheadAt: at ?? undefined });
    } else {
      pendingPlayheadMs = null;
    }
    if (!demoMode && !state.data.updated_at) {
      window.setTimeout(() => void refresh(), 4000);
    }
  } catch (error) {
    console.error("refresh", error);
    if (demoMode) document.getElementById("updated")!.textContent = "Demo archive unavailable";
  } finally {
    refreshInFlight = false;
  }
}

function paint(opts?: { force?: boolean }): void {
  const data = state.data;
  if (!data) return;
  // Play/scrub: coalesce to ~12 fps so we do not setData the heatmap 60 times/sec.
  if (!opts?.force && state.playing) {
    const now = performance.now();
    if (now - lastPaintAt < PLAY_PAINT_MS) {
      if (!paintRaf) {
        paintRaf = requestAnimationFrame(() => {
          paintRaf = 0;
          paint({ force: true });
        });
      }
      return;
    }
    lastPaintAt = now;
  }
  const incidents = filteredIncidents();
  const heatIds = new Set(incidents.map((i) => i.id));
  // High Sensitivity paints every returned pixel, including looks with no incident yet.
  let heat =
    state.view === "fine" ? parsedHeat : parsedHeat.filter((p) => p.incidentId && heatIds.has(p.incidentId));
  // Open fire always embers — its cells can lose a Europe-wide FRP cap.
  if (state.detail && !state.detail.archive) {
    heat = mergeHeatPoints(heat, heatPointsFromDetections(state.detail.detections));
  }
  if (state.playing && heat.length > PLAY_HEAT_MAX) heat = decimateHeat(heat, PLAY_HEAT_MAX);
  applyData(map, { ...data, incidents }, state.playheadMs, state.openId, heat);
  if (state.detail && !panel.classList.contains("hidden") && (state.page === "theater" || !state.detail.archive)) {
    applyPlayheadUpdate(panel, {
      playheadMs: state.playheadMs,
      untilMs: Date.parse(data.window.until),
      detections: state.detail.detections,
      liveFrp: state.detail.incident.frp_mw,
      archive: Boolean(state.detail.archive),
      nowMs: dataNow(),
      historical: demoMode,
    });
  }
  syncEventSat();
  syncEventBurned();
  syncLookMarks();
  const now = dataNow();
  const untilMs = Date.parse(data.window.until);
  const live = state.playheadMs >= untilMs - 30_000;
  if (state.page === "theater") {
    setPlayheadLabel(live);
    return;
  }
  // Header chips stay on the window clock so opening a fire does not make 58 → 115.
  const chips = chipCounts(data.incidents, untilMs);
  const chipKey = `${chips.fires}|${chips.suspected}|${chips.campaigns}|${chips.rising}|${chips.new}|${live}|${data.updated_at}`;
  if (chipKey !== lastChipKey) {
    lastChipKey = chipKey;
    document.getElementById("stat-fires")!.textContent = String(chips.fires);
    document.getElementById("stat-suspected")!.textContent = String(chips.suspected);
    document.getElementById("stat-imp")!.textContent = String(chips.campaigns);
    document.getElementById("stat-rising")!.textContent = String(chips.rising);
    document.getElementById("stat-new")!.textContent = String(chips.new);
    document.getElementById("updated")!.textContent = demoMode ? DEMO_LABEL : updatedLabel(data.updated_at, now);
    const stale = !demoMode && (!data.updated_at || now - Date.parse(data.updated_at) > 2 * 3600 * 1000);
    document.getElementById("live-pill")!.classList.toggle("stale", stale);
  }
  setPlayheadLabel(live);
}

/** Clock when scrubbing; LIVE at the window end. The control always jumps to now. */
function setPlayheadLabel(live: boolean): void {
  const el = document.getElementById("playhead")!;
  el.textContent = demoMode ? (live && state.page === "map" ? "END OF DAY" : `${new Date(state.playheadMs).toISOString().slice(11, 16)} UTC`) : live ? "LIVE" : formatClock(new Date(state.playheadMs).toISOString());
  el.classList.toggle("is-live", live && !demoMode);
}

/** Window end is “now” on the map, last look in theater. */
function jumpToNow(): void {
  if (!state.data) return;
  stopPlay();
  rewindPlayhead(Date.parse(state.data.window.until));
  paint();
  writeHash();
}

/** Bumps on stop / restart so a leftover rAF tick cannot keep running. */
let playGen = 0;

/** Put the playhead at first look (or window start), clamped to the current window. */
function rewindPlayhead(atMs?: number): void {
  if (!state.data) return;
  const t0 = Date.parse(state.data.window.since);
  const t1 = Date.parse(state.data.window.until);
  const span = Math.max(1, t1 - t0);
  const raw = atMs != null && Number.isFinite(atMs) ? atMs : t0;
  state.playheadMs = Math.min(Math.max(raw, t0), t1);
  slider.value = String(Math.round(((state.playheadMs - t0) / span) * 1000));
}

function startPlay(): void {
  if (!state.data) return;
  const gen = ++playGen;
  state.playing = true;
  document.getElementById("play")!.textContent = "Pause";
  const t0 = Date.parse(state.data.window.since);
  const t1 = Date.parse(state.data.window.until);
  // From LIVE, skip empty hours before this fire's first look in the window.
  if (state.playheadMs >= t1 - 1000) {
    state.playheadMs = firstSelectedLookMs(t0, t1) ?? t0;
  }
  // Sat stays as you left it. Local photo replay only if Sat is already on.
  const event = selectedIncident();
  if (state.layers.sat && event) flyToIncident(map, event, EVENT_SAT_ZOOM);
  syncEventSat();
  const startHead = state.playheadMs;
  // Remaining window plays in ~20 s so a late first look is not a 3-second clip.
  const playSpan = Math.max(1, t1 - startHead);
  const started = performance.now();

  const tick = (now: number) => {
    if (gen !== playGen || !state.playing || !state.data) return;
    const elapsed = now - started;
    state.playheadMs = Math.min(t1, startHead + (elapsed / 20000) * playSpan);
    slider.value = String(Math.round(((state.playheadMs - t0) / Math.max(1, t1 - t0)) * 1000));
    paint();
    if (state.playheadMs >= t1) stopPlay();
    else requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

function stopPlay(): void {
  const wasPlaying = state.playing;
  playGen += 1;
  state.playing = false;
  document.getElementById("play")!.textContent = "Play";
  // Back to the live GeoColour frame — replay does not leave Sat stepping.
  syncEventSat();
  if (wasPlaying && state.openId) writeHash();
}

function selectedIncident(): Incident | null {
  if (!state.openId) return null;
  return state.data?.incidents.find((i) => i.id === state.openId) ?? state.detail?.incident ?? null;
}

/** Overpass times for the open fire — panel detections if loaded, else heat. */
function selectedLookTimes(): number[] {
  if (state.detail?.detections.length) {
    return state.detail.detections.map((d) => Date.parse(d.observed_at));
  }
  const id = state.openId;
  if (!id) return [];
  const out: number[] = [];
  for (const p of parsedHeat) {
    if (p.incidentId === id) out.push(p.t);
  }
  return out;
}

function firstSelectedLookMs(sinceMs: number, untilMs: number): number | null {
  return firstLookInWindow(selectedLookTimes(), sinceMs, untilMs);
}

/** Faded look marks on the scrubber — this fire's signals in the current window. */
function syncLookMarks(): void {
  const el = document.getElementById("look-marks");
  if (!el || !state.data) return;
  if (!state.openId) {
    if (lastMarksKey !== "off") {
      lastMarksKey = "off";
      el.innerHTML = "";
      el.classList.add("hidden");
    }
    return;
  }
  const t0 = Date.parse(state.data.window.since);
  const t1 = Date.parse(state.data.window.until);
  const marks = eventLookMarks(selectedLookTimes(), t0, t1);
  const key = `${state.openId}:${t0}:${t1}:${marks.length}:${marks[0] ?? 0}:${marks[marks.length - 1] ?? 0}`;
  if (key === lastMarksKey) return;
  lastMarksKey = key;
  const span = Math.max(1, t1 - t0);
  el.innerHTML = marks
    .map((t) => `<i style="left:${(((t - t0) / span) * 100).toFixed(2)}%"></i>`)
    .join("");
  el.classList.toggle("hidden", marks.length === 0);
}

function syncBurnedLegend(): void {
  if (demoMode) return;
  const show = Boolean(state.layers.ba || state.openId);
  document.getElementById("ba-legend")?.classList.toggle("hidden", !show);
}

/** Local GeoColour movie only when Sat is on, Play is running, and a fire is open. Otherwise Sat stays on the live frame. */
function syncEventSat(): void {
  if (demoMode) return;
  const event = selectedIncident();
  const until = state.data ? Date.parse(state.data.window.until) : Date.now();
  if (!state.layers.sat || !state.playing || !event) {
    if (lastSatKey !== "off") {
      lastSatKey = "off";
      setEventSat(map, null);
      setLayerVisible(map, "sat", state.layers.sat);
    }
    return;
  }
  const since = state.data ? Date.parse(state.data.window.since) : until - 24 * 3600 * 1000;
  const slot = replayProductSlotIso(state.playheadMs, until - since, 10, 20, until);
  const key = `on:${event.id}:${slot}`;
  if (key === lastSatKey) return;
  lastSatKey = key;
  // Hide continent XYZ tiles while the local frame is up — those are the expensive ones.
  setLayerVisible(map, "sat", false);
  setEventSat(map, {
    url: mtgGeoFrameUrl(event.lat, event.lon, slot),
    coordinates: mtgGeoCoordinates(event.lat, event.lon),
  });
  const nextMs = state.playheadMs + Math.max(10 * 60 * 1000, (until - since) / 24);
  const nextSlot = replayProductSlotIso(nextMs, until - since, 10, 20, until);
  if (nextSlot !== slot) {
    const pre = new Image();
    pre.src = mtgGeoFrameUrl(event.lat, event.lon, nextSlot);
  }
}

/**
 * Local EFFIS scar around the open fire. Not the Burned button — that one
 * still paints Europe. Skip when Burned is already on so the same pixels
 * do not stack. Box follows the heat hull; quantized so Play does not refetch.
 */
function syncEventBurned(): void {
  if (demoMode) return;
  const note = document.getElementById("ba-event-note");
  const event = selectedIncident();
  const hide = () => {
    burnedGen += 1;
    if (lastBurnedKey !== "off") {
      lastBurnedKey = "off";
      setEventBurned(map, null);
    }
    note?.classList.add("hidden");
    syncBurnedLegend();
  };
  if (!event || state.layers.ba) {
    hide();
    return;
  }
  const frame = eventBurnedFrame(incidentHeatPoints(parsedHeat, event.id), [event.lon, event.lat]);
  if (!frame) {
    hide();
    return;
  }
  const key = `${event.id}:${frame.key}`;
  if (key !== lastBurnedKey) {
    lastBurnedKey = key;
    const gen = ++burnedGen;
    // Hatch off the main thread of paint() — raw EFFIS red is a fallback if CORS fails.
    void styleBurnedOverlay(frame.url).then((url) => {
      if (gen !== burnedGen) return;
      setEventBurned(map, { url, coordinates: frame.coordinates });
    });
  }
  note?.classList.add("hidden");
  syncBurnedLegend();
}

async function openIncident(
  id: string,
  fallback?: EventCard | null,
  opts?: { autoplay?: boolean; playheadAt?: number; theater?: boolean },
): Promise<void> {
  const gen = ++panelGen;
  stopWindPoll();
  const res = await fetch(demoMode ? demoJsonPath(`incidents/${encodeURIComponent(id)}`) : `/api/incidents/${encodeURIComponent(id)}`);
  let detail: IncidentDetail | null = null;
  if (res.ok && (!demoMode || res.headers.get("content-type")?.includes("json"))) {
    const body = (await res.json()) as IncidentDetail;
    if (body.incident) detail = body;
  } else if (fallback) {
    // Live row is gone — show the saved catalog snapshot, not a fake heat tape.
    detail = detailFromCatalog(fallback);
  }
  if (!detail || gen !== panelGen) return;
  const archive = Boolean(detail.archive);
  state.openId = id;
  state.detail = detail;
  document.body.classList.add("incident-open");
  panel.classList.remove("hidden");
  drawer.classList.add("hidden");
  if (state.page === "theater" || opts?.theater) {
    await enterTheater(detail, gen, opts);
    return;
  }
  applyRouteMeta(detail);
  paint();
  paintPanel();
  if (archive) {
    // Live Europe tape is empty for this id. Sidebar replay uses saved heat when we have it.
    flyToIncident(map, detail.incident);
    void loadCameras(gen, detail.incident.lat, detail.incident.lon);
    writeHash();
    return;
  }
  // Close camera on this fire's own cells — the Europe payload may have dropped them.
  flyToIncidentHeat(map, detail.incident, incidentHeatPoints(heatPointsFromDetections(detail.detections), id));
  if (opts?.playheadAt != null && state.data) {
    rewindPlayhead(opts.playheadAt);
    paint();
  } else if (opts?.autoplay && state.data) {
    // Events / map pick: start at the first look in this window, then play once.
    const t0 = Date.parse(state.data.window.since);
    const t1 = Date.parse(state.data.window.until);
    const first =
      firstLookInWindow(
        detail.detections.map((d) => Date.parse(d.observed_at)),
        t0,
        t1,
      ) ?? Date.parse(detail.incident.first_seen);
    rewindPlayhead(first);
    startPlay();
  }
  startWindPoll(gen, detail.incident);
  void loadCameras(gen, detail.incident.lat, detail.incident.lon);
  void loadFwi(gen, detail.incident.lon, detail.incident.lat);
  writeHash();
}

function paintPanel(): void {
  if (!state.detail) return;
  const following = state.follows.some((f) => f.kind === "incident" && f.target === state.detail!.incident.id);
  const email = state.follows.find((f) => f.kind === "incident" && f.target === state.detail!.incident.id)?.email ?? null;
  const archiveAt = Date.parse(state.detail.last_observed || state.detail.incident.last_seen);
  renderPanel(panel, state.detail, {
    following,
    nowMs: dataNow(),
    historical: demoMode,
    email,
    nearby: state.data?.incidents ?? [],
    playheadMs: state.page === "theater" || !state.detail.archive ? state.playheadMs : archiveAt,
    untilMs: state.data ? Date.parse(state.data.window.until) : Date.now(),
    back: state.page === "theater",
  });
  // New English HTML — Google only walks it if we re-fire the combo.
  refreshTranslation();
}

function closeIncident(): void {
  if (state.page === "theater") {
    go(eventsListPath());
    return;
  }
  resetOpenIncident();
  writeHash();
  paint();
}

/** Clear the sidebar without changing the page. */
function resetOpenIncident(): void {
  panelGen += 1;
  stopWindPoll();
  stopPanel(panel);
  closeMailPop();
  state.openId = null;
  state.detail = null;
  panel.classList.add("hidden");
  document.body.classList.remove("incident-open");
  if (state.page === "map") applyRouteMeta(null);
}

function openMailPop(): void {
  if (!state.detail) return;
  const id = state.detail.incident.id;
  const follow = state.follows.find((f) => f.kind === "incident" && f.target === id);
  document.getElementById("mail-pop-place")!.textContent = state.detail.place_name || state.detail.incident.label;
  mailInput.value = follow?.email ?? "";
  document.getElementById("mail-pop-stop")?.classList.toggle("hidden", !follow);
  mailPop.classList.remove("hidden");
  mailInput.focus();
}

function closeMailPop(): void {
  mailPop.classList.add("hidden");
}

/** Saves the address and follows this fire if the bell is not already on. */
async function saveMailAlert(): Promise<void> {
  if (!state.openId) return;
  const email = mailInput.value.trim() || null;
  const following = state.follows.some((f) => f.kind === "incident" && f.target === state.openId);
  if (!following) await ensureNotifyPermission();
  await setFollow("incident", state.openId, email);
  await refreshFollows();
  paintPanel();
  closeMailPop();
}

/** Poll this fire only. Age label ticks every 30s; Open-Meteo is hit every 2 min. */
function startWindPoll(gen: number, incident: Incident): void {
  if (demoMode) return;
  stopWindPoll();
  let ticks = 0;
  windTimer = window.setInterval(() => {
    if (gen !== panelGen) return;
    ticks += 1;
    if (state.detail?.wind) {
    applyWindUpdate(panel, state.detail.wind, Date.now());
    refreshTranslation();
  }
    if (ticks % 4 === 0) void pollWind(gen, incident.lat, incident.lon);
  }, 30_000);
}

function stopWindPoll(): void {
  if (windTimer) {
    window.clearInterval(windTimer);
    windTimer = 0;
  }
}

async function pollWind(gen: number, lat: number, lon: number): Promise<void> {
  const res = await fetch(`/api/wind?lat=${lat}&lon=${lon}`);
  if (!res.ok || gen !== panelGen || !state.detail) return;
  const body = (await res.json()) as { wind: WindSample | null };
  if (gen !== panelGen || !state.detail) return;
  state.detail.wind = body.wind;
  applyWindUpdate(panel, body.wind, Date.now());
  refreshTranslation();
}

async function loadCameras(gen: number, lat: number, lon: number): Promise<void> {
  if (demoMode) return;
  const res = await fetch(`/api/cameras?lat=${lat}&lon=${lon}`);
  if (!res.ok || gen !== panelGen || !state.detail) return;
  const body = (await res.json()) as { cameras: WebcamLink[] };
  if (gen !== panelGen || !state.detail) return;
  state.detail.cameras = body.cameras ?? [];
  applyCamerasUpdate(panel, state.detail.cameras);
  refreshTranslation();
}

function openLightbox(src: string, caption: string): void {
  const img = document.getElementById("lightbox-img") as HTMLImageElement;
  img.src = src;
  document.getElementById("lightbox-cap")!.textContent = caption;
  lightbox.classList.remove("hidden");
}

function closeLightbox(): void {
  lightbox.classList.add("hidden");
  (document.getElementById("lightbox-img") as HTMLImageElement).src = "";
}

/** Bell: arm browser notifications, follow the fire, then ask for email. */
async function followAndAlert(id: string): Promise<void> {
  if (demoMode) return;
  const on = state.follows.some((f) => f.kind === "incident" && f.target === id);
  if (!on) {
    await ensureNotifyPermission();
    await setFollow("incident", id);
    await refreshFollows();
    paintPanel();
  }
  openMailPop();
}

async function stopIncidentAlerts(): Promise<void> {
  if (!state.openId) return;
  await unsetFollow("incident", state.openId);
  await refreshFollows();
  paintPanel();
  closeMailPop();
}

async function toggleBelgium(): Promise<void> {
  if (demoMode) return;
  const on = watchingBelgium();
  if (on) await unsetFollow("geofence", "BE");
  else {
    await ensureNotifyPermission();
    await setFollow("geofence", "BE");
  }
  await refreshFollows();
}

function watchingBelgium(): boolean {
  return state.follows.some((f) => f.kind === "geofence" && f.target === "BE");
}

async function refreshFollows(): Promise<void> {
  if (demoMode) return;
  state.follows = await loadFollows();
  const watching = watchingBelgium();
  document.getElementById("watch-be")!.classList.toggle("on", watching);
  document.getElementById("watch-be")!.textContent = watching ? "Watching BE" : "Watch BE";
  document.getElementById("watch-be-drawer")!.classList.toggle("on", watching);
  document.getElementById("watch-be-drawer")!.textContent = watching ? "Watching Belgium" : "Watch Belgium";
  if (state.detail) paintPanel();
}

async function refreshAlerts(): Promise<void> {
  if (demoMode) return;
  const alerts = await loadAlerts();
  state.alerts = alerts;
  document.getElementById("alert-count")!.textContent = String(alerts.length);
  const list = document.getElementById("alerts-list")!;
  if (alerts.length === 0) {
    list.innerHTML = `<li class="fine">No alerts yet. Watch Belgium or follow a fire.</li>`;
  } else {
    list.innerHTML = alerts
      .map(
        (a) => `<li>
          <button type="button" data-incident="${escapeAttr(a.incident_id)}">
            <strong>${escapeAttr(a.label)}</strong><br />
            ${escapeAttr(a.message)}
            <span class="meta">${escapeAttr(formatClock(a.created_at))}</span>
          </button>
        </li>`,
      )
      .join("");
  }
  notifyNew(alerts);
  refreshTranslation();
}

function notifyNew(alerts: AlertRow[]): void {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  const seen = readSeenAlertIds();
  const fresh = alerts.filter((a) => !seen.has(a.id));
  for (const a of fresh.slice(0, 3)) {
    try {
      new Notification("IGNIS", { body: a.message });
    } catch {
      /* Safari private mode, or notifications blocked after grant */
    }
    seen.add(a.id);
  }
  for (const a of alerts) seen.add(a.id);
  writeSeenAlertIds(seen);
}

function filteredIncidents(): Incident[] {
  const data = state.data;
  if (!data) return [];
  return visibleIncidents(data.incidents, state.playheadMs, state.view, Date.parse(data.window.until));
}

function permalinkPlayheadMs(): number | undefined {
  if (!state.openId || !state.detail) return undefined;
  if (state.page !== "theater" && state.detail.archive) {
    const at = Date.parse(state.detail.last_observed || state.detail.incident.last_seen);
    return Number.isFinite(at) ? at : undefined;
  }
  if (!state.data) return undefined;
  const until = Date.parse(state.data.window.until);
  // LIVE / last look stays off the hash so a shared link opens on the current clock.
  if (state.playheadMs >= until - 30_000) return undefined;
  return state.playheadMs;
}

function writeHash(): void {
  if (state.page === "events") {
    writeEventsUrl();
    return;
  }
  if (state.page === "theater") {
    writeTheaterUrl();
    return;
  }
  const next = formatPermalink({
    window: state.window,
    place: state.place ?? undefined,
    view: state.view,
    id: state.openId ?? undefined,
    t: permalinkPlayheadMs(),
  });
  const url = `${appPath("/")}${next}`;
  if (`${location.pathname}${location.search}${location.hash}` !== url) {
    history.replaceState(null, "", url);
  }
}

function eventsListPath(): string {
  const month = state.eventsMonth;
  return appPath(eventsCatalogPath({
    country: state.eventsCountry,
    // Current month stays off the URL so /events is “this month”.
    month: month === currentEventMonth(dataNow()) ? "" : month,
    q: state.eventsQuery.trim(),
  }));
}

function writeEventsUrl(): void {
  const url = eventsListPath();
  if (`${location.pathname}${location.search}` !== url) history.replaceState(null, "", url);
}

function writeTheaterUrl(): void {
  if (!state.openId) return;
  const t = permalinkPlayheadMs();
  const hash = t != null ? `#t=${formatPermalinkTime(t)}` : "";
  const url = `${appPath(eventsTheaterPath(state.openId))}${hash}`;
  if (`${location.pathname}${location.hash}` !== url) history.replaceState(null, "", url);
}

function syncPlaceButtons(): void {
  document.querySelectorAll("#places button[data-place]").forEach((el) => {
    el.classList.toggle("on", state.place != null && (el as HTMLButtonElement).dataset.place === state.place);
  });
}

function go(url: string): void {
  url = appPath(url);
  if (`${location.pathname}${location.search}${location.hash}` === url) {
    void applyRoute();
    return;
  }
  history.pushState(null, "", url);
  void applyRoute();
}

function syncPageChrome(): void {
  const page = state.page;
  document.body.classList.toggle("page-map", page === "map");
  document.body.classList.toggle("page-events", page === "events");
  document.body.classList.toggle("page-theater", page === "theater");
  eventsPage.classList.toggle("hidden", page !== "events");
  eventsPage.hidden = page !== "events";
  theaterBar.classList.toggle("hidden", page !== "theater");
  document.getElementById("nav-map")!.classList.toggle("on", page === "map");
  document.getElementById("nav-events")!.classList.toggle("on", page !== "map");
  // Head tags only — visible chrome stays as-is.
  applyRouteMeta(page === "events" ? null : state.detail);
}

/** Tracker title when idle; news name when a fire is open on the map or in theater. */
function applyRouteMeta(detail: IncidentDetail | null | undefined): void {
  const origin = publicOriginFromHost(location.hostname);
  if (demoMode) {
    const meta = pageMeta({ page: "map", origin, noindex: true });
    const title = `${detail?.place_name || "Europe fires"} · ${DEMO_LABEL} · IGNIS Demo`;
    const description = `Recorded satellite fire activity across Europe on ${DEMO_LABEL}. Static demonstration.`;
    applyDocumentMeta({ ...meta, title, ogTitle: title, canonical: `${origin}${location.pathname}`, description, ogDescription: description, robots: "noindex, nofollow" });
    return;
  }
  if (detail) {
    const card = state.eventCards.find((c) => c.id === detail.incident.id);
    applyDocumentMeta(
      pageMeta({
        page: state.page === "theater" ? "theater" : "map",
        origin,
        event: eventSeoFromDetail(detail, card?.peak_frp_mw),
      }),
    );
    return;
  }
  applyDocumentMeta(pageMeta({ page: state.page === "events" ? "events" : "map", origin }));
}

async function applyRoute(): Promise<void> {
  const route = parseAppPath(location.pathname, location.search);
  stopPlay();
  if (route.page === "events") {
    resetOpenIncident();
    state.page = "events";
    state.eventsCountry = route.country;
    state.eventsMonth = route.month || currentEventMonth(dataNow());
    state.eventsQuery = route.q;
    state.data = null;
    parsedHeat = [];
    syncPageChrome();
    void loadEvents();
    return;
  }
  if (route.page === "theater") {
    state.page = "theater";
    syncPageChrome();
    requestAnimationFrame(() => map.resize());
    await openTheater(route.id);
    return;
  }
  resetOpenIncident();
  state.page = "map";
  syncPageChrome();
  requestAnimationFrame(() => map.resize());
  const hash = parsePermalink(location.hash);
  if (hash.id) pendingOpenId = hash.id;
  if (hash.t != null) pendingPlayheadMs = hash.t;
  if (!state.data || state.data.incidents.length <= 1) {
    // Leaving theater leaves a one-fire tape — reload Europe.
    firesEtag = "";
    void refresh();
    return;
  }
  paint({ force: true });
  if (pendingOpenId) {
    const id = pendingOpenId;
    pendingOpenId = null;
    const at = pendingPlayheadMs;
    pendingPlayheadMs = null;
    void openIncident(id, undefined, { playheadAt: at ?? undefined });
  }
}

async function openTheater(id: string): Promise<void> {
  pendingTheaterId = null;
  if (!state.eventCards.length) await loadEvents();
  const card = state.eventCards.find((c) => c.id === id) ?? null;
  await openIncident(id, card, {
    autoplay: pendingPlayheadMs == null,
    playheadAt: pendingPlayheadMs ?? undefined,
    theater: true,
  });
  pendingPlayheadMs = null;
}

/** Swap the Europe tape for this fire's saved / live pixels and play that span. */
async function enterTheater(
  detail: IncidentDetail,
  gen: number,
  opts?: { autoplay?: boolean; playheadAt?: number },
): Promise<void> {
  const tape = firesFromDetail(detail);
  state.page = "theater";
  state.data = tape;
  parsedHeat = parseHeatPoints(tape.heat);
  const place = detail.place_name || detail.incident.label;
  theaterTitle.textContent = place;
  applyRouteMeta(detail);
  theaterLive.classList.toggle("hidden", Boolean(detail.archive));
  theaterLive.href = `${appPath("/")}${formatPermalink({ id: detail.incident.id })}`;
  document.getElementById("updated")!.textContent = detail.archive ? "archive" : "this fire";
  syncPageChrome();
  map.resize();
  paint({ force: true });
  paintPanel();
  const pts = incidentHeatPoints(parsedHeat, detail.incident.id);
  // Immediate fit — the catalog hid the map; a 900 ms fly from Europe looks like a miss.
  if (pts.length >= 2) flyToIncidentHeat(map, detail.incident, pts, { immediate: true });
  else map.jumpTo({ center: [detail.incident.lon, detail.incident.lat], zoom: EVENT_HEAT_ZOOM });
  requestAnimationFrame(() => map.resize());
  const first =
    firstLookInWindow(
      detail.detections.map((d) => Date.parse(d.observed_at)),
      Date.parse(tape.window.since),
      Date.parse(tape.window.until),
    ) ?? Date.parse(detail.incident.first_seen);
  rewindPlayhead(opts?.playheadAt ?? first);
  paint({ force: true });
  if (opts?.autoplay !== false && opts?.playheadAt == null) startPlay();
  if (!detail.archive) {
    startWindPoll(gen, detail.incident);
    void loadFwi(gen, detail.incident.lon, detail.incident.lat);
  }
  void loadCameras(gen, detail.incident.lat, detail.incident.lon);
  writeTheaterUrl();
}

async function loadEvents(): Promise<void> {
  const list = document.getElementById("events-list");
  const show = state.page === "events";
  seedEventCards();
  if (show && list) {
    if (state.eventCards.length) paintEvents();
    else list.innerHTML = `<p class="fine">Loading…</p>`;
  }
  try {
    const res = await fetch(demoMode ? demoJsonPath("events") : "/api/events", { headers: { accept: "application/json" } });
    const type = res.headers.get("content-type") ?? "";
    if (!res.ok || !type.includes("json")) throw new Error(`events ${res.status}`);
    const body = (await res.json()) as { events?: EventCard[] };
    if (!Array.isArray(body.events)) throw new Error("events payload");
    state.eventCards = body.events;
    if (show) paintEvents();
  } catch (err) {
    console.error("loadEvents", err);
    seedEventCards();
    if (!show || !list) return;
    if (state.eventCards.length) paintEvents();
    else list.innerHTML = `<p class="fine">No catalogued events yet.</p>`;
  }
}

function seedEventCards(): void {
  if (state.eventCards.length > 0 || !state.data) return;
  state.eventCards = cardsFromIncidents(state.data.incidents, dataNow());
}

function paintEvents(): void {
  const list = document.getElementById("events-list");
  if (state.page === "events") {
    document.getElementById("updated")!.textContent = `${state.eventCards.length} catalogued`;
  }
  if (!list) return;
  const search = document.getElementById("events-search") as HTMLInputElement | null;
  const keepSearch = document.activeElement === search;
  const start = search?.selectionStart ?? null;
  const end = search?.selectionEnd ?? null;
  try {
    list.innerHTML = renderEventsList(
      state.eventCards,
      { country: state.eventsCountry, month: state.eventsMonth, q: state.eventsQuery },
      dataNow(),
    );
    const next = document.getElementById("events-search") as HTMLInputElement | null;
    if (keepSearch && next) {
      next.focus();
      if (start != null) next.setSelectionRange(start, end ?? start);
    }
    fillEventPlaceNames(list);
    fillBasemapThumbnails(list);
    refreshTranslation();
  } catch (err) {
    console.error("paintEvents", err);
    list.innerHTML = `<p class="events-empty">No catalogued events yet.</p>`;
  }
}

function syncViewButtons(): void {
  document.querySelectorAll("#stats button[data-view]").forEach((el) => {
    const v = (el as HTMLButtonElement).dataset.view || "";
    el.classList.toggle("on", v === state.view);
  });
}

const WINDOW_KEYS: Record<string, WindowKey> = {
  "1": "1h",
  "6": "6h",
  "2": "24h",
  "4": "48h",
  "7": "7d",
};

function setWindow(key: WindowKey): void {
  if (demoMode && (key === "48h" || key === "7d")) return;
  if (state.page !== "map") return;
  if (state.window === key) return;
  state.window = key;
  const btn = document.querySelector(`[data-window="${key}"]`) as HTMLButtonElement | null;
  if (btn) setOn("#windows button", btn);
  firesEtag = "";
  stopPlay();
  writeHash();
  void refresh();
}

function cycleIncident(dir: number): void {
  if (state.page === "theater") {
    const list = state.eventCards;
    if (list.length === 0) return;
    const idx = state.openId ? list.findIndex((i) => i.id === state.openId) : -1;
    const next = list[(idx + dir + list.length) % list.length];
    if (next) go(eventsTheaterPath(next.id));
    return;
  }
  const list = filteredIncidents()
    .slice()
    .sort((a, b) => b.frp_mw - a.frp_mw);
  if (list.length === 0) return;
  const idx = state.openId ? list.findIndex((i) => i.id === state.openId) : -1;
  const next = list[(idx + dir + list.length) % list.length];
  if (next) void openIncident(next.id);
}

async function fetchFwi(lon: number, lat: number) {
  try {
    const res = await fetch(effisFwiInfoUrl(lon, lat, utcIsoDate(state.fwiDay)));
    if (!res.ok) return null;
    return parseEffisFwiHtml(await res.text());
  } catch {
    return null;
  }
}

async function showFwiProbe(lon: number, lat: number): Promise<void> {
  const el = document.getElementById("fwi-probe")!;
  el.textContent = "FWI…";
  el.classList.remove("hidden");
  const sample = await fetchFwi(lon, lat);
  if (!sample) {
    el.textContent = "FWI unavailable here";
    return;
  }
  const cls = fwiClass(sample.fwi);
  el.innerHTML = `<strong style="color:${cls.color}">FWI ${sample.fwi.toFixed(1)}</strong> · ${escapeAttr(cls.label)}`;
}

function hideFwiProbe(): void {
  document.getElementById("fwi-probe")!.classList.add("hidden");
}

async function loadFwi(gen: number, lon: number, lat: number): Promise<void> {
  if (demoMode) return;
  const sample = await fetchFwi(lon, lat);
  if (gen !== panelGen || !state.detail) return;
  applyFwiUpdate(panel, sample);
  refreshTranslation();
}

async function copyText(text: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
  }
}

function setOn(selector: string, active: HTMLButtonElement): void {
  document.querySelectorAll(selector).forEach((el) => el.classList.remove("on"));
  active.classList.add("on");
}

function escapeAttr(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]!);
}
